import React, { useEffect, useMemo, useRef } from "react";
import { Carousel as AntdCarousel } from "antd";
import { type CarouselRef } from "antd/es/carousel";

interface IProps {
    children: React.ReactNode;
    showTwoColumns: boolean;
    dots?: boolean;
    autoScrol?: boolean;
    afterChange?: (ref: React.RefObject<CarouselRef>) => (current: number) => void;
}

export default function MyCarousel({
    children,
    showTwoColumns,
    dots = false,
    autoScrol = false,
    afterChange = () => () => { },
}: IProps): JSX.Element {
    const ref = useRef<CarouselRef>(null);

    const items = useMemo(() => React.Children.toArray(children), [children]);

    const forceRefresh = () => {
        const slider = ref.current?.innerSlider;
        if (slider?.onWindowResized) slider.onWindowResized();
        if (ref.current?.goTo) ref.current.goTo(slider?.state?.currentSlide ?? 0, true);
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            forceRefresh();
            requestAnimationFrame(forceRefresh);
        });
    }, [items.length, showTwoColumns]);

    useEffect(() => {
        const onResize = () => forceRefresh();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
        <AntdCarousel
            ref={ref}
            dots={dots}
            autoplay={autoScrol}
            afterChange={afterChange(ref)}
            waitForAnimate
            slidesToShow={1}
            slidesToScroll={1}
            variableWidth={false}
            centerMode={false}
        >
            {items.map((child, i) => (
                <div key={(child as any)?.key ?? i}>{child}</div>
            ))}
        </AntdCarousel>
    );
}
